import { Button, Center, Loader, Menu, SegmentedControl, TextInput } from '@mantine/core';
import {
  checkTemplate,
  LANGS,
  MAIL_TEMPLATE_KEYS,
  MAIL_TEMPLATES,
  PLACEHOLDERS,
  type Lang,
  type MailTemplateBody,
  type MailTemplateKey,
  type PlaceholderKey,
} from '@reditelji/shared';
import type { Editor } from '@tiptap/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEvent } from '../api/hooks';
import { useSession } from '../api/session';
import { isDirty, sameTrimmed, type FieldSame } from '../lib/draft';
import { plural } from '../lib/ui';
import {
  useMailMutations,
  useMailPreview,
  useMailSends,
  useMailTemplates,
  type MailPreview,
  type MailSendState,
  type MailTemplateRow,
} from '../api/mail';
import { useVolunteers } from '../api/volunteers';
import { DayMailCard } from './DayMailCard';
import { IngestStatus } from './IngestStatus';
import { MailPreviewPane } from './MailPreview';
import { useNoticeDraft, type HandoffNote } from './noticeDraft';
import { Notices } from './Notices';
import { RecipientMatrix } from './Recipients';
import { countSent, lastSent, shortDate } from './sendState';
import { BodyEditor, PlaceholderPalette } from './TemplateEditor';
import { Warnings } from './Warnings';
import './mail.css';
import { useCoordinatorLabel } from '../lib/people';

/**
 * Sporočila — what volunteers get by e-mail, and who has had it.
 *
 * Four views because they answer four different questions. "Predloge" is
 * about wording: what do the four standing mails say, in both languages, and
 * what does one look like for a real person. "Prejemniki" is about coverage:
 * who has not heard from us yet, and can they be caught up in one action.
 * "Obvestila" is the mail that fits none of the four — written once, for the
 * people picked for it, and kept as a record of what was said. "Nabiralnik" is
 * about the other direction — whether what volunteers write is reaching us at
 * all, which is the only one that can fail silently.
 */
export function MailPage() {
  const [view, setView] = useState<'predloge' | 'prejemniki' | 'obvestila' | 'nabiralnik'>(
    'predloge',
  );
  const templates = useMailTemplates();
  const sends = useMailSends();
  const volunteers = useVolunteers();
  // Held here rather than in the view, so a Prejemniki selection can be handed
  // to it, and so switching views keeps what is being written.
  const { me } = useSession();
  const notice = useNoticeDraft(me?.id ?? null);
  const [noticeSelected, setNoticeSelected] = useState('new');
  const [handoffNote, setHandoffNote] = useState<HandoffNote | null>(null);

  if (templates.isPending || sends.isPending) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }
  if (!templates.data || !sends.data) return null;

  return (
    <div className="ml-page">
      <div className="ml-head">
        <div>
          <div className="ml-title">Sporočila</div>
          <div className="ml-lede">
            Kaj prostovoljci dobijo po e-pošti in kdo je kaj že prejel.
          </div>
        </div>
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v)}
          data={[
            { value: 'predloge', label: 'Predloge' },
            { value: 'prejemniki', label: 'Prejemniki' },
            { value: 'obvestila', label: 'Obvestila' },
            { value: 'nabiralnik', label: 'Nabiralnik' },
          ]}
        />
      </div>

      {view === 'predloge' ? (
        <Templates rows={templates.data} sends={sends.data} />
      ) : view === 'prejemniki' ? (
        <RecipientMatrix
          sends={sends.data}
          onCompose={(ids) => {
            setHandoffNote(notice.replaceRecipients(ids, volunteers.data ?? []));
            setNoticeSelected('new');
            setView('obvestila');
          }}
        />
      ) : view === 'obvestila' ? (
        <Notices
          notice={notice}
          onClear={() => {
            notice.clear();
            setHandoffNote(null);
          }}
          selected={noticeSelected}
          onSelect={setNoticeSelected}
          handoffNote={handoffNote}
          onDismissHandoff={() => setHandoffNote(null)}
        />
      ) : (
        <IngestStatus />
      )}
    </div>
  );
}

function Templates({ rows, sends }: { rows: MailTemplateRow[]; sends: MailSendState[] }) {
  const [selected, setSelected] = useState<MailTemplateKey>('uvodno');
  // Held above the form, which remounts per template: whoever the coordinator
  // is previewing for is the person they are thinking about, and that outlasts
  // a switch from one mail to the next.
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const row = rows.find((r) => r.key === selected);

  return (
    <div className="ml-layout">
      <nav className="ml-rail">
        <div className="ml-eyebrow">PREDLOGE</div>
        {MAIL_TEMPLATE_KEYS.map((key) => {
          const def = MAIL_TEMPLATES[key];
          const count = countSent(key, sends);
          const last = lastSent(key, sends);
          return (
            <button
              key={key}
              type="button"
              className={`ml-railitem${key === selected ? ' ml-railitem--active' : ''}`}
              onClick={() => setSelected(key)}
            >
              <span className="ml-railitem-name">{def.label}</span>
              <span className="ml-railitem-state">
                {count === 0
                  ? 'še ni bilo poslano'
                  : `${count} ${recipients(count)} · nazadnje ${shortDate(last!)}`}
              </span>
            </button>
          );
        })}
      </nav>

      {row ? (
        <TemplateForm
          key={row.key}
          row={row}
          previewFor={previewFor}
          onPreviewFor={setPreviewFor}
        />
      ) : null}
    </div>
  );
}

/** '3 prejemniki' — the shared rule, filled in. */
const recipients = (n: number) =>
  plural(n, 'prejemnik', 'prejemnika', 'prejemniki', 'prejemnikov');

/** The subject is stored trimmed; the body exactly as the editor wrote it. */
const BODY_SAME: FieldSame<MailTemplateBody> = { subject: sameTrimmed };

/**
 * One template, both languages.
 *
 * The template row from the server is the baseline: whatever it last confirmed
 * is what "Shrani" compares against, so it settles once the write lands.
 */
function TemplateForm({
  row,
  previewFor,
  onPreviewFor,
}: {
  row: MailTemplateRow;
  previewFor: string | null;
  onPreviewFor: (id: string | null) => void;
}) {
  const labelOf = useCoordinatorLabel();
  const def = MAIL_TEMPLATES[row.key];
  const event = useEvent();
  const volunteers = useVolunteers();
  const { save, reset, translate, testSend } = useMailMutations();
  const previewMutation = useMailPreview();

  const [lang, setLang] = useState<Lang>('sl');
  const [draft, setDraft] = useState<Record<Lang, MailTemplateBody>>({ sl: row.sl, en: row.en });
  const [editor, setEditor] = useState<Editor | null>(null);
  const [preview, setPreview] = useState<MailPreview | null>(null);
  // With nobody picked, the sample can read as a mail to a shared mailbox —
  // two people on one address — so the wording is checked for a couple too.
  // A real volunteer's household is found on its own.
  const [household, setHousehold] = useState(false);
  // Bumped whenever the draft is replaced rather than edited — undo, restore
  // defaults, a fresh translation — so the editor knows to re-seed itself.
  const [seed, setSeed] = useState(0);
  const replaceDraft = (next: Record<Lang, MailTemplateBody>) => {
    setDraft(next);
    setSeed((n) => n + 1);
  };

  const current = draft[lang];
  const dirty = LANGS.some((l) => isDirty(draft[l], row[l], BODY_SAME));
  const check = checkTemplate(row.key, current.subject, current.body);
  const aiEnabled = event.data?.ai.enabled ?? false;

  const set = (patch: Partial<MailTemplateBody>) =>
    setDraft((d) => ({ ...d, [lang]: { ...d[lang], ...patch } }));

  // The preview follows the draft, one render behind the typing.
  //
  // Sequenced because a preview with a volunteer picked runs several queries:
  // an earlier request can land after a later one and leave the pane — and the
  // warnings under the editor — describing a body that is no longer there.
  const { mutate: renderPreview } = previewMutation;
  const latest = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const seq = (latest.current += 1);
      renderPreview(
        {
          key: row.key,
          lang,
          subject: current.subject,
          body: current.body,
          volunteerId: previewFor,
          household: previewFor === null && household,
        },
        {
          onSuccess: (out) => {
            if (seq === latest.current) setPreview(out);
          },
        },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [renderPreview, row.key, lang, current.subject, current.body, previewFor, household]);

  const volunteerOptions = useMemo(
    () =>
      (volunteers.data ?? [])
        .filter((v) => v.active)
        .map((v) => ({ value: v.id, label: v.name })),
    [volunteers.data],
  );

  const insert = (key: PlaceholderKey) => {
    if (!editor) return;
    if (PLACEHOLDERS[key].block) {
      editor
        .chain()
        .focus()
        .insertContent({ type: 'placeholderBlock', attrs: { key } })
        .run();
    } else {
      editor.chain().focus().insertPlaceholder(key).run();
    }
  };

  return (
    <>
      <section className="ml-editor">
        <div className="ml-editor-head">
          <div>
            <div className="ml-editor-name">{def.label}</div>
            <div className="ml-hint">{def.lede}</div>
          </div>
          <SegmentedControl
            size="xs"
            value={lang}
            onChange={(v) => setLang(v)}
            data={[
              { value: 'sl', label: 'Slovensko' },
              { value: 'en', label: 'English' },
            ]}
          />
        </div>

        {row.key === 'dnevno' ? <DayMailCard /> : null}

        {lang === 'en' && aiEnabled ? (
          <div className="ml-translate">
            <span className="ml-hint">
              Angleščino dobijo prostovoljci, ki so izbrali angleški jezik.
            </span>
            <Button
              size="compact-xs"
              variant="default"
              loading={translate.isPending}
              onClick={() =>
                translate.mutate(
                  { key: row.key, subject: draft.sl.subject, body: draft.sl.body },
                  {
                    onSuccess: (out) =>
                      replaceDraft({
                        ...draft,
                        en: { subject: out.subject, body: out.body },
                      }),
                  },
                )
              }
            >
              Prevedi iz slovenščine
            </Button>
          </div>
        ) : null}

        <TextInput
          size="sm"
          label="Zadeva"
          placeholder="Zadeva sporočila"
          value={current.subject}
          onChange={(e) => set({ subject: e.currentTarget.value })}
        />
        <div className="ml-hint" style={{ marginTop: 3 }}>
          Oznake v zadevi piši z zavitimi oklepaji, npr. <code>{'{{dogodek}}'}</code>.
        </div>

        <div className="ml-editor-body">
          <BodyEditor
            value={current.body}
            onChange={(html) => set({ body: html })}
            templateKey={row.key}
            docKey={`${row.key}:${lang}:${seed}`}
            onEditor={setEditor}
          />
        </div>

        <Warnings check={check} unresolved={preview?.unresolved ?? []} />

        <div className="ml-actions">
          <span className="ml-saved">
            {row.updatedByName
              ? `Nazadnje uredil ${labelOf(row.updatedById, row.updatedByName)}, ${shortDate(row.updatedAt)}.`
              : 'Privzeto besedilo.'}
          </span>
          <div style={{ flex: 1 }} />
          <Button
            size="xs"
            variant="default"
            loading={testSend.isPending}
            onClick={() =>
              testSend.mutate({
                key: row.key,
                lang,
                subject: current.subject,
                body: current.body,
                volunteerId: previewFor,
                household: previewFor === null && household,
              })
            }
          >
            Pošlji poskusno sebi
          </Button>
          <Menu position="top-end" withArrow>
            <Menu.Target>
              <Button size="xs" variant="subtle">
                ⋮
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  reset.mutate(
                    { key: row.key, updatedAt: row.updatedAt },
                    { onSuccess: (fresh) => replaceDraft({ sl: fresh.sl, en: fresh.en }) },
                  )
                }
              >
                Povrni privzeto besedilo
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          <Button
            size="xs"
            variant="default"
            disabled={!dirty}
            onClick={() => replaceDraft({ sl: row.sl, en: row.en })}
          >
            Razveljavi
          </Button>
          <Button
            size="xs"
            disabled={!dirty || current.subject.trim() === ''}
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                key: row.key,
                sl: draft.sl,
                en: draft.en,
                updatedAt: row.updatedAt,
              })
            }
          >
            Shrani
          </Button>
        </div>

        <div className="ml-eyebrow" style={{ marginTop: 18 }}>
          OZNAKE
        </div>
        <div className="ml-hint" style={{ marginBottom: 8 }}>
          Klik vstavi oznako na mesto kazalca. Ob pošiljanju jo aplikacija zamenja s podatkom
          prostovoljca.
        </div>
        <PlaceholderPalette templateKey={row.key} onInsert={insert} />
      </section>

      <MailPreviewPane
        preview={preview}
        pending={previewMutation.isPending}
        volunteerId={previewFor}
        onVolunteer={onPreviewFor}
        volunteers={volunteerOptions}
        household={household}
        onHousehold={setHousehold}
      />
    </>
  );
}
